import { get, useFormContext } from "react-hook-form";

import Markdown from "components/markdown/Markdown";
import ReadMoreDocsLink from "components/ReadMoreDocsLink";
import Box from "ds/components/Box";
import FormField from "ds/components/Form/Field";
import Input from "ds/components/Input";
import TooltipModalBody from "ds/components/TooltipModal/Body";
import { TooltipModalTitle } from "ds/components/TooltipModal/Title";
import Typography from "ds/components/Typography";
import { getDocsUrl } from "utils/getDocsUrl";

import styles from "./styles.module.css";

type FormPathFieldProps = {
  name: string;
  disabled?: boolean;
};

const tooltipText = `
The relative path where the file will be mounted inside the container. It must not start or end with a slash.

Every Spacelift workload gets a dedicated directory \`/mnt/workspace/\`, which also serves as a root for all the mounted files.

Your Git repository is cloned into \`/mnt/workspace/source/\`, and this is the default working directory, unless overridden by a project root setting.
- **Terraform files/ Scripts (e.g. \`.tfvars\`)** must be mounted inside \`/mnt/workspace/source/\` or \`/mnt/workspace/source/<project_root>\` to be automatically picked up by Terraform.
- Files mounted outside of the working directory are available, but may need to be referenced manually.
`;

const validatePath = (value: string) => {
  if (value.startsWith("/")) {
    return "Mount path must be relative";
  }

  if (value.endsWith("/")) {
    return "Mount path must point to a file";
  }

  return true;
};

const FormPathField = ({ name, disabled }: FormPathFieldProps) => {
  const { register, formState } = useFormContext();
  const error = get(formState.errors, name);

  return (
    <FormField
      label="Path"
      error={error?.message}
      fullWidth
      tooltipInfoVariant="modal"
      tooltipMode="parent"
      tooltipWidthMode="maxWidthXl"
      tooltipInfo={
        <>
          <TooltipModalTitle>Mount path</TooltipModalTitle>
          <TooltipModalBody align="start">
            <Markdown markup={tooltipText} className={styles.pathFieldTooltipText} />
            <ReadMoreDocsLink
              docsUrl={getDocsUrl("/concepts/configuration/environment.html#project-structure")}
            />
          </TooltipModalBody>
        </>
      }
    >
      {({ ariaInputProps }) => (
        <Box align="center" gap="small">
          <Typography variant="p-body3" tag="span">
            /mnt/workspace/
          </Typography>

          <Input
            placeholder="Enter path here..."
            error={!!error}
            disabled={disabled}
            {...register(name, {
              required: "Path field is required.",
              validate: validatePath,
            })}
            {...ariaInputProps}
          />
        </Box>
      )}
    </FormField>
  );
};

export default FormPathField;
